import { useMemo, useState } from 'react'
import {
  minimumDetectableEffect,
  power as computePower,
  requiredSampleSize,
} from '../lib/stats/power'
import { alphaFromConfidence } from '../lib/stats/proportion'
import type { Tail } from '../lib/stats/types'
import {
  formatCount,
  formatDuration,
  formatLift,
  formatPercent,
  formatPoints,
} from '../lib/format'
import { NumberField } from './NumberField'
import { SegmentedControl } from './SegmentedControl'

type Mode = 'size' | 'power'
type EffectKind = 'relative' | 'absolute'

const MODE_OPTIONS = [
  {
    value: 'size' as Mode,
    label: '必要人数を求める',
    title: 'テスト開始前に、何人集めれば判定できるかを計算する。',
  },
  {
    value: 'power' as Mode,
    label: 'この人数の実力を見る',
    title: '既に集まった人数で、どれくらいの差なら検出できるかを計算する。',
  },
]

const EFFECT_OPTIONS = [
  { value: 'relative' as EffectKind, label: '相対 (%)' },
  { value: 'absolute' as EffectKind, label: '絶対 (pt)' },
]

const CONFIDENCE_OPTIONS = [
  { value: '0.9', label: '90%' },
  { value: '0.95', label: '95%' },
  { value: '0.99', label: '99%' },
] as const

const POWER_OPTIONS = [
  { value: '0.8', label: '80%' },
  { value: '0.9', label: '90%' },
  { value: '0.95', label: '95%' },
] as const

const TAIL_OPTIONS = [
  { value: 'two-sided' as Tail, label: '両側' },
  { value: 'greater' as Tail, label: '片側 (増加)' },
]

export function SampleSizeView() {
  const [mode, setMode] = useState<Mode>('size')
  const [baselinePercent, setBaselinePercent] = useState(5)
  const [effectKind, setEffectKind] = useState<EffectKind>('relative')
  const [effectValue, setEffectValue] = useState(10)
  const [confidence, setConfidence] = useState(0.95)
  const [targetPower, setTargetPower] = useState(0.8)
  const [allocationRatio, setAllocationRatio] = useState(1)
  const [dailyVisitors, setDailyVisitors] = useState(2000)
  const [achievedN1, setAchievedN1] = useState(5000)
  const [achievedN2, setAchievedN2] = useState(5000)

  const baseline = baselinePercent / 100
  const alpha = alphaFromConfidence(confidence)
  const [selectedTail, setSelectedTail] = useState<Tail>('two-sided')

  /** 入力された効果量から、比較群の想定比率を求める。 */
  const targetRate = useMemo(() => {
    if (!Number.isFinite(baseline) || !Number.isFinite(effectValue)) return NaN
    return effectKind === 'relative'
      ? baseline * (1 + effectValue / 100)
      : baseline + effectValue / 100
  }, [baseline, effectKind, effectValue])

  const inputsValid =
    Number.isFinite(baseline) &&
    baseline > 0 &&
    baseline < 1 &&
    Number.isFinite(targetRate) &&
    targetRate > 0 &&
    targetRate < 1 &&
    Number.isFinite(allocationRatio) &&
    allocationRatio > 0

  const sizeResult = useMemo(() => {
    if (mode !== 'size' || !inputsValid) return null
    return requiredSampleSize({
      baselineRate: baseline,
      targetRate,
      alpha,
      power: targetPower,
      allocationRatio,
      tail: selectedTail,
    })
  }, [
    mode,
    inputsValid,
    baseline,
    targetRate,
    alpha,
    targetPower,
    allocationRatio,
    selectedTail,
  ])

  const powerResult = useMemo(() => {
    if (mode !== 'power') return null
    if (
      !Number.isFinite(baseline) ||
      baseline <= 0 ||
      baseline >= 1 ||
      !Number.isFinite(achievedN1) ||
      !Number.isFinite(achievedN2) ||
      achievedN1 < 1 ||
      achievedN2 < 1
    ) {
      return null
    }
    const mde = minimumDetectableEffect({
      baselineRate: baseline,
      n1: achievedN1,
      n2: achievedN2,
      alpha,
      power: targetPower,
      tail: selectedTail,
      direction: 'increase',
    })
    const achievedPower = Number.isFinite(targetRate)
      ? computePower({
          baselineRate: baseline,
          targetRate,
          n1: achievedN1,
          n2: achievedN2,
          alpha,
          tail: selectedTail,
        })
      : NaN
    return { mde, achievedPower }
  }, [
    mode,
    baseline,
    achievedN1,
    achievedN2,
    alpha,
    targetPower,
    selectedTail,
    targetRate,
  ])

  const days =
    sizeResult && Number.isFinite(dailyVisitors) && dailyVisitors > 0
      ? sizeResult.total / dailyVisitors
      : NaN

  return (
    <>
      <section className="card">
        <h2 className="card-title">何を求めるか</h2>
        <p className="card-hint">
          テストを始める前なら「必要人数」、すでにデータが集まっているなら「この人数の実力」を
          選んでください。後者は「有意差なし」の解釈に使います。
        </p>
        <SegmentedControl
          label="計算する内容"
          value={mode}
          options={MODE_OPTIONS}
          onChange={setMode}
        />
      </section>

      <section className="card">
        <h2 className="card-title">前提</h2>
        <div className="form-grid">
          <NumberField
            label="現状の CVR"
            unit="(%)"
            value={baselinePercent}
            min={0.01}
            max={99.99}
            step={0.1}
            onChange={setBaselinePercent}
            error={
              Number.isFinite(baselinePercent) &&
              (baselinePercent <= 0 || baselinePercent >= 100)
                ? '0 より大きく 100 未満'
                : undefined
            }
          />
          {mode === 'power' && (
            <>
              <NumberField
                label="対照群の人数"
                unit="(人)"
                value={achievedN1}
                min={1}
                step={100}
                onChange={setAchievedN1}
              />
              <NumberField
                label="比較群の人数"
                unit="(人)"
                value={achievedN2}
                min={1}
                step={100}
                onChange={setAchievedN2}
              />
            </>
          )}
        </div>

        <div className="settings-row" style={{ marginTop: 18 }}>
          <SegmentedControl
            label={mode === 'size' ? '検出したい効果' : '知りたい効果'}
            value={effectKind}
            options={EFFECT_OPTIONS}
            onChange={setEffectKind}
          />
          <NumberField
            label="効果量"
            unit={effectKind === 'relative' ? '(%)' : '(pt)'}
            value={effectValue}
            step={effectKind === 'relative' ? 1 : 0.1}
            onChange={setEffectValue}
            error={
              inputsValid || !Number.isFinite(effectValue)
                ? undefined
                : '想定 CVR が 0〜100% の範囲に収まりません'
            }
          />
          {mode === 'size' && (
            <NumberField
              label="割付比 (比較群 / 対照群)"
              value={allocationRatio}
              min={0.1}
              step={0.5}
              onChange={setAllocationRatio}
            />
          )}
        </div>

        <p className="card-hint" style={{ margin: '14px 0 0' }}>
          効果量を{' '}
          {effectKind === 'relative'
            ? `相対 ${Number.isFinite(effectValue) ? effectValue : '—'}%`
            : `絶対 ${Number.isFinite(effectValue) ? effectValue : '—'} pt`}{' '}
          とすると、比較群の想定 CVR は{' '}
          <strong>{formatPercent(targetRate)}</strong> です。
        </p>
      </section>

      <section className="card">
        <h2 className="card-title">判定の基準</h2>
        <div className="settings-row">
          <SegmentedControl
            label="信頼水準"
            value={String(confidence)}
            options={CONFIDENCE_OPTIONS}
            onChange={(value) => setConfidence(Number(value))}
          />
          <SegmentedControl
            label="検出力"
            value={String(targetPower)}
            options={POWER_OPTIONS}
            onChange={(value) => setTargetPower(Number(value))}
          />
          <SegmentedControl
            label="検定の向き"
            value={selectedTail}
            options={TAIL_OPTIONS}
            onChange={setSelectedTail}
          />
        </div>
      </section>

      {mode === 'size' && (
        <section className="card">
          <h2 className="card-title">必要なサンプルサイズ</h2>
          {sizeResult ? (
            <>
              <div className="result-grid">
                <div className="result-tile result-tile-primary">
                  <div className="result-tile-label">対照群</div>
                  <div className="result-tile-value">
                    {formatCount(sizeResult.n1)}
                  </div>
                  <div className="result-tile-sub">人</div>
                </div>
                <div className="result-tile result-tile-primary">
                  <div className="result-tile-label">比較群</div>
                  <div className="result-tile-value">
                    {formatCount(sizeResult.n2)}
                  </div>
                  <div className="result-tile-sub">人</div>
                </div>
                <div className="result-tile">
                  <div className="result-tile-label">合計</div>
                  <div className="result-tile-value">
                    {formatCount(sizeResult.total)}
                  </div>
                  <div className="result-tile-sub">人</div>
                </div>
                <div className="result-tile">
                  <div className="result-tile-label">想定期間</div>
                  <div className="result-tile-value" style={{ fontSize: 20 }}>
                    {formatDuration(days)}
                  </div>
                  <div className="result-tile-sub">
                    1 日 {formatCount(dailyVisitors)} 人として
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 18, maxWidth: 260 }}>
                <NumberField
                  label="1 日あたりの対象人数"
                  unit="(人)"
                  value={dailyVisitors}
                  min={1}
                  step={100}
                  onChange={setDailyVisitors}
                />
              </div>

              <p className="card-hint" style={{ margin: '16px 0 0' }}>
                現状 {formatPercent(baseline)} の CVR が{' '}
                {formatPercent(targetRate)} に変わったとき、
                {formatPercent(confidence, 0)} 信頼水準・検出力{' '}
                {formatPercent(targetPower, 0)} で検出するのに必要な人数です。
                この人数に達する前に結果を覗いて判断すると、偽陽性の確率が
                設定した水準より高くなります。
              </p>
            </>
          ) : (
            <p className="empty-state">
              前提の入力を確認してください。効果量が 0 だと必要人数は定義できません。
            </p>
          )}
        </section>
      )}

      {mode === 'power' && (
        <section className="card">
          <h2 className="card-title">この人数で分かること</h2>
          {powerResult ? (
            <>
              <div className="result-grid">
                <div className="result-tile result-tile-primary">
                  <div className="result-tile-label">
                    検出できる最小の差 (MDE)
                  </div>
                  <div className="result-tile-value">
                    {powerResult.mde ? formatPoints(powerResult.mde.absolute) : '—'}
                  </div>
                  <div className="result-tile-sub">
                    {powerResult.mde
                      ? `相対 ${formatLift(powerResult.mde.relative)} / 想定 CVR ${formatPercent(powerResult.mde.targetRate)}`
                      : 'この人数では検出力を確保できません'}
                  </div>
                </div>
                <div className="result-tile">
                  <div className="result-tile-label">
                    指定した効果に対する検出力
                  </div>
                  <div className="result-tile-value">
                    {Number.isFinite(powerResult.achievedPower)
                      ? formatPercent(powerResult.achievedPower, 1)
                      : '—'}
                  </div>
                  <div className="result-tile-sub">
                    想定 CVR {formatPercent(targetRate)} のとき
                  </div>
                </div>
              </div>

              <p className="card-hint" style={{ margin: '16px 0 0' }}>
                MDE は「この人数なら、これ以上の差があれば検出力{' '}
                {formatPercent(targetPower, 0)} で捉えられる」という境界です。
                有意差が出なかったテストで観測された差が MDE より小さければ、
                結論は「差がない」ではなく「この人数では判断できない」になります。
              </p>
            </>
          ) : (
            <p className="empty-state">人数と現状の CVR を入力してください。</p>
          )}
        </section>
      )}

      <section className="card">
        <h2 className="card-title">用語</h2>
        <dl className="notes">
          <dt>信頼水準 / 有意水準</dt>
          <dd>
            信頼水準 {formatPercent(confidence, 0)} は、有意水準{' '}
            {formatPercent(alpha, 0)} と同じことです。本当は差がないのに
            「差がある」と誤って判定してしまう確率の上限を指します。
          </dd>
          <dt>検出力 (power)</dt>
          <dd>
            本当に差があるときに、それを取りこぼさず「差がある」と判定できる確率です。
            80% なら、実際に効果があっても 5 回に 1 回は見逃します。
          </dd>
          <dt>MDE (最小検出可能効果)</dt>
          <dd>
            決めた人数・信頼水準・検出力のもとで、検出できる最小の効果量です。
            これより小さい差は、実際に存在しても統計的には捉えられません。
          </dd>
          <dt>割付比</dt>
          <dd>
            対照群に対する比較群の人数の比です。1 が最も効率的で、偏らせると
            同じ検出力を得るのに必要な合計人数が増えます。
          </dd>
        </dl>
      </section>
    </>
  )
}
